'use server'

import { apiRequest, ApiResponse } from '../request'
import type { ApprovalCompany, BulkResult } from './billing-approval-api'
import { requestBillingRefund } from './financial-billing-api'
import type { RefundSubmitData } from '@/components/billing/request-refund-modal'

const BASE = 'financial/billing-refunds'

/**
 * Por onde o dinheiro volta ao cliente:
 * - `BAAS`: estorna a cobrança no provedor, pela conta digital. Só existe quando o
 *   pagamento ENTROU por lá (`canRefundViaBaas`);
 * - `ALREADY_REFUNDED`: o dinheiro já foi devolvido por fora (espécie, outro banco).
 *   Registra a devolução e a instituição, sem tocar na conta digital.
 */
export type RefundMode = 'BAAS' | 'ALREADY_REFUNDED'

export interface RefundRequestItem {
    /** Filial dona da solicitação — a fila é da organização inteira. */
    company?: ApprovalCompany | null
    id: number
    billingId: number
    createdAt: string
    payload?: {
        refundAmount?: number
        refundReason?: string
    } | null
    requestedBy?: { id: number; name: string } | null
    /** Marcação "já estornado por fora" (outro banco), feita antes da aprovação. */
    externalRefund?: {
        via: string
        at: string
        byId: number
        byName: string | null
    } | null
    billing: {
        id: number
        status: string
        amount: number
        paidAmount: number | null
        refundedAmount: number | null
        paidAt: string | null
        description: string | null
        paymentMethod: string | null
        client?: { id: number; name: string } | null
        partner?: { id: number; name: string; tradingName: string | null } | null
        /** Pagamento entrou pelo provedor — o estorno automático está disponível. */
        canRefundViaBaas: boolean
        /**
         * Cartão parcelado: no provedor a venda é um PARCELAMENTO. O estorno (integral
         * ou parcial) é feito nele, e o provedor decide de quais parcelas o valor sai.
         */
        isProviderInstallment: boolean
        /** Quanto ainda pode ser devolvido (pago menos o que já voltou). */
        refundableAmount: number
    }
}

export interface ApproveRefundOptions {
    mode: RefundMode
    /** Obrigatória em ALREADY_REFUNDED. */
    refundedVia?: string
    /** Em centavos. Omitido, usa o valor pedido na solicitação. */
    amount?: number
}

export async function getPendingRefundRequests(
    search?: string,
): Promise<ApiResponse<RefundRequestItem[]>> {
    return apiRequest<RefundRequestItem[]>(BASE, {
        params: search ? { search } : {},
    })
}

export type ApprovedRefundItem = RefundRequestItem & {
    approvedBy?: { id: number; name: string } | null
    approvedAt?: string | null
}

export async function getApprovedRefundRequests(
    page = 1,
    limit = 20,
    search?: string,
): Promise<
    ApiResponse<{
        items: ApprovedRefundItem[]
        total: number
        page: number
        limit: number
    }>
> {
    return apiRequest(`${BASE}/history`, {
        params: { page, limit, ...(search ? { search } : {}) },
    })
}

export async function approveRefund(
    id: number,
    password: string,
    options: ApproveRefundOptions,
): Promise<ApiResponse<RefundRequestItem>> {
    return apiRequest<RefundRequestItem>(
        `${BASE}/${id}/approve`,
        { data: { password, ...options } },
        'post',
    )
}

export async function rejectRefund(
    id: number,
    reason: string,
): Promise<ApiResponse<RefundRequestItem>> {
    return apiRequest<RefundRequestItem>(`${BASE}/${id}/reject`, { data: { reason } }, 'post')
}

/** Marca o estorno como já feito por fora (outro banco) — não aprova, só registra. */
export async function markRefundExternallyRefunded(
    id: number,
    refundedVia: string,
): Promise<ApiResponse<RefundRequestItem>> {
    return apiRequest<RefundRequestItem>(
        `${BASE}/${id}/external-refund`,
        { data: { refundedVia } },
        'post',
    )
}

/** Remove a marcação "já estornado por fora". */
export async function clearRefundExternalRefund(
    id: number,
): Promise<ApiResponse<RefundRequestItem>> {
    return apiRequest<RefundRequestItem>(`${BASE}/${id}/external-refund`, {}, 'delete')
}

/**
 * Aprova vários estornos. Cada solicitação devolve o valor que pediu — o lote não
 * aceita um valor único, que seria o valor errado em todas menos uma.
 */
export async function bulkApproveRefunds(
    ids: number[],
    password: string,
    options: Omit<ApproveRefundOptions, 'amount'>,
): Promise<ApiResponse<BulkResult>> {
    return apiRequest<BulkResult>(
        `${BASE}/bulk-approve`,
        { data: { ids, password, ...options } },
        'post',
    )
}

export async function bulkRejectRefunds(
    ids: number[],
    reason: string,
): Promise<ApiResponse<BulkResult>> {
    return apiRequest<BulkResult>(`${BASE}/bulk-reject`, { data: { ids, reason } }, 'post')
}

/**
 * Recibo do estorno em PDF.
 *
 * Vem por blob (e não por link direto) porque a rota exige o JWT do `apiRequest` —
 * um `<a href>` para a API sairia sem token e responderia 401.
 */
async function fetchRefundReceiptBlob(id: number): Promise<Blob> {
    const { getClient } = await import('@/lib/api/client')
    const client = await getClient()
    const response = await client.get(`${BASE}/${id}/receipt.pdf`, {
        responseType: 'blob',
    })
    return new Blob([response.data], { type: 'application/pdf' })
}

/**
 * URL temporária do recibo, para visualizar e imprimir pelo viewer do navegador.
 * Quem chama é responsável por `URL.revokeObjectURL` ao fechar.
 */
export async function getRefundReceiptUrl(id: number): Promise<string> {
    return URL.createObjectURL(await fetchRefundReceiptBlob(id))
}

/** Baixa o recibo do estorno como arquivo. */
export async function downloadRefundReceiptPdf(id: number): Promise<void> {
    const { downloadBlob } = await import('@/lib/download-blob')
    downloadBlob(await fetchRefundReceiptBlob(id), `recibo-estorno-${id}.pdf`)
}

/**
 * Estorna a cobrança NA HORA, sem passar pela fila.
 *
 * Só funciona com "Estorno sem aprovação" ligado em Configurações Financeiras >
 * Recebimentos — o backend relê a política e recusa se ela estiver desligada, então
 * esconder o caminho na tela é conveniência, nunca a autorização.
 *
 * Pede numa chamada só o que a fila pede em dois momentos: valor e motivo (da
 * solicitação) e modo, instituição e senha (da aprovação).
 */
export async function refundBillingInstant(
    billingId: number,
    data: {
        /** Centavos. */
        amount: number
        reason: string
        password: string
        mode?: RefundMode
        /** Obrigatória em ALREADY_REFUNDED. */
        refundedVia?: string
    }
): Promise<ApiResponse<{ refundInProgress?: boolean }>> {
    return apiRequest<{ refundInProgress?: boolean }>(
        `${BASE}/instant/${billingId}`,
        { data },
        'post'
    )
}

/**
 * Envia o estorno pelo caminho que a empresa configurou.
 *
 * O modal já sabe qual dos dois montou (`instant`), e é o tipo que decide o
 * endpoint — a alternativa seria cada tela que abre o modal repetir esse `if`, e
 * são várias (cobrança, exame, consulta, procedimento, teleconsulta).
 *
 * Devolve a mensagem pronta para o toast: "estornado" e "enviado para aprovação"
 * são desfechos diferentes e o operador precisa saber qual aconteceu.
 */
export async function submitBillingRefund(
    billingId: number,
    data: RefundSubmitData
): Promise<{ success: boolean; message: string; error?: string }> {
    if (!data.instant) {
        const res = await requestBillingRefund(billingId, {
            amount: data.amount,
            reason: data.reason,
        })
        return {
            success: res.success,
            message:
                res.result?.message ||
                'Solicitação de estorno enviada para aprovação de um operador financeiro',
            error: res.error,
        }
    }

    const res = await refundBillingInstant(billingId, {
        amount: data.amount,
        reason: data.reason,
        password: data.password,
        mode: data.mode,
        refundedVia: data.refundedVia,
    })

    // O provedor pode ter apenas ACEITADO o pedido: no cartão a devolução é
    // processada depois (até dez dias úteis) e ainda pode ser negada. Dizer
    // "devolvido" aí faria o operador prometer ao cliente algo que o banco ainda
    // não fez.
    const inProgress = !!res.result?.refundInProgress

    return {
        success: res.success,
        message: inProgress
            ? 'Estorno solicitado ao provedor. A devolução é confirmada quando ele concluir — a cobrança segue como paga até lá.'
            : data.mode === 'BAAS'
              ? 'Estorno realizado — o valor foi devolvido ao cliente.'
              : 'Estorno registrado.',
        error: res.error,
    }
}
